'use client'
import NextLink from 'next/link'
import ColorScheme from '@/components/colorScheme'
import type { ColorScheme as ColorSchemeType } from '@/sanity.types'
import type { SpokeSummary } from '@/lib/youth-sports'
import { posthog } from '@/lib/posthog'
import { useYouthSportsAnalytics } from './YouthSportsAnalyticsContext'
import { SPORT_ILLUSTRATIONS } from '@/components/youth-sports/illustrations'
import { renderInlineMarkdown } from '@/lib/inline-markdown'

const LINK_SCHEMES: ColorSchemeType[] = [
  'lilac',
  'sky',
  'grape',
  'tangerine',
  'pea',
]

type Props = {
  currentSport: string   // slug, e.g. "soccer"
  currentCity: string    // slug, e.g. "san-francisco"
  currentVariant?: string // e.g. "boys" or "girls" for lacrosse
  allPublished: SpokeSummary[]
  heading?: string
  intro?: string
  outro?: string
  sameSportOnly?: boolean
}

export default function RelatedPages({
  currentSport,
  currentCity,
  currentVariant,
  allPublished,
  heading,
  intro,
  outro,
  sameSportOnly = false,
}: Props) {
  const { sport, city, pageType, variant } = useYouthSportsAnalytics()

  function trackClick(destination: SpokeSummary) {
    posthog.capture('related_page_click', {
      sport,
      city,
      pageType,
      source: 'web_marketing',
      ...(variant ? { variant } : {}),
      destination_sport: destination.sport,
      destination_city: destination.city,
    })
  }

  const sameCity = sameSportOnly
    ? []
    : allPublished.filter(
        (p) =>
          p.city === currentCity &&
          p.status === 'published' &&
          (p.sport !== currentSport ||
            (currentVariant != null && p.variant !== currentVariant)),
      )
  const sameSport = allPublished.filter(
    (p) =>
      p.sport === currentSport &&
      p.city !== currentCity &&
      p.status === 'published'
  )

  if (!sameCity.length && !sameSport.length) return null

  const cardClassName =
    'bg-(--color-background) text-(--color-foreground) p-4 rounded ' +
    'flex flex-col gap-4 transition-transform duration-200 hover:scale-[1.04] hover:z-10'

  return (
    <section aria-labelledby="related-heading">
      <h2 id="related-heading" className="ys-section-h mb-7.5">
        {heading ?? 'More Guides'}
      </h2>

      {intro && (
        <p className="text-lg leading-[1.5] mb-7.5">
          {renderInlineMarkdown(intro)}
        </p>
      )}

      {sameCity.length > 0 && (
        <div className={sameSport.length > 0 ? 'mb-10' : ''}>
          <p className="eyebrow mb-5">Other sports in {sameCity[0].displayCity}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {sameCity.map((p, i) => {
              const Illustration = SPORT_ILLUSTRATIONS[p.sport]
              return (
              <ColorScheme
                key={`${p.sport}-${p.city}-${p.variant ?? ''}`}
                colorScheme={LINK_SCHEMES[i % LINK_SCHEMES.length]}
              >
                <NextLink
                  href={p.url}
                  onClick={() => trackClick(p)}
                  className={cardClassName}
                >
                  {Illustration && (
                    <div className="self-start w-12 shrink-0" aria-hidden="true">
                      <Illustration className="h-auto w-full" />
                    </div>
                  )}
                  <p className="text-lg font-medium leading-[1.3]">{p.h1}</p>
                </NextLink>
              </ColorScheme>
              )
            })}
          </div>
        </div>
      )}

      {sameSport.length > 0 && (
        <div>
          <p className="eyebrow mb-5">
            {sameSport[0].displaySport} in other locations
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {sameSport.map((p, i) => {
              const Illustration = SPORT_ILLUSTRATIONS[currentSport]
              return (
              <ColorScheme
                key={`${p.sport}-${p.city}-${p.variant ?? ''}`}
                colorScheme={LINK_SCHEMES[(i + 2) % LINK_SCHEMES.length]}
              >
                <NextLink
                  href={p.url}
                  onClick={() => trackClick(p)}
                  className={cardClassName}
                >
                  {Illustration && (
                    <div className="self-start w-12 shrink-0" aria-hidden="true">
                      <Illustration className="h-auto w-full" />
                    </div>
                  )}
                  <p className="text-lg font-medium leading-[1.3]">{p.h1}</p>
                </NextLink>
              </ColorScheme>
              )
            })}
          </div>
        </div>
      )}

      {outro && (
        <p className="text-lg leading-[1.5] mt-7.5">
          {renderInlineMarkdown(outro)}
        </p>
      )}
    </section>
  )
}
